// Advanced Capacity Mode full-game resolution (Phase 1).
// Mirrors the Classic resolveGame flow (disaster draws, risk
// outlook weighting, subsidy state, carbon tax and COP 31) but
// resolves each year with the two-layer adequacy checks from
// src/engine/advancedCapacity.js. Classic resolveGame is untouched.

import { resolveAdvancedYear } from './advancedCapacity.js';
import { rollVariableOutputs } from './variableOutput.js';
import { lookupCarbonBracket, pickCarbonMultiplier } from './carbonTax.js';
import {
  drawDisasters,
  assignRiskTiers,
  tierWeightsToCardWeights,
  resolvePersistentDisasterTarget
} from './disasters.js';
import { solveTierWeights } from './weightSolver.js';
import { GAME_YEARS, DISASTER_DRAWS_PER_YEAR } from '../data/constants.js';
import { CARBON_TAX_BRACKETS } from '../data/brackets.js';
import { EFFECT_KIND } from '../data/disasters.js';
import { ADVANCED_DISASTER_POOL } from '../data/advancedDisasters.js';
import {
  ADVANCED_PLANT_BY_ID,
  ADVANCED_UNITS,
  ADVANCED_CARBON_YEAR_WEIGHTS,
  ADVANCED_STARTING_PORTFOLIO
} from '../data/advancedCapacity.js';
import {
  GAME_MODE,
  ADVANCED_CAPACITY_GAME_VERSION
} from '../data/modes.js';

// Remove one portfolio instance per id listed in `ids`. Ignores ids
// with no remaining instance (a retire plan can name a plant that a
// disaster-free build never acquired).
export function removePortfolioInstances(portfolio, ids) {
  const next = [...portfolio];
  const removed = [];
  for (const id of ids) {
    const idx = next.indexOf(id);
    if (idx === -1) continue;
    next.splice(idx, 1);
    removed.push(id);
  }
  return { portfolio: next, removed };
}

export function resolveAdvancedGame({
  buildPlan,
  retirePlan = {},
  worldEvents,
  archetypeRng,
  plantsById = ADVANCED_PLANT_BY_ID,
  disasterDeck = ADVANCED_DISASTER_POOL,
  brackets = CARBON_TAX_BRACKETS,
  initialSubsidyActive = true,
  // Section 16.15: Advanced Mode starts with an inherited fossil
  // fleet the player did not choose. Default-valued so every caller
  // picks it up unchanged, and so a test can pass [] to isolate.
  // These are ordinary owned plants in every respect - they pay opex,
  // emit, occupy slots, take disasters, and can be retired.
  initialPortfolio = ADVANCED_STARTING_PORTFOLIO,
  // Passed through to each year so calibration can sweep the declining
  // firm-capacity schedule without editing src/data.
  declineSchedule = undefined
}) {
  const { cop30 } = worldEvents;

  let portfolio = [...initialPortfolio];
  let remainingDeck = [...disasterDeck];
  let subsidyActive = initialSubsidyActive;
  let persistentDisasters = [];
  let carriedCharge = {};
  let carriedChargeUnits = {};
  const subsidyByYear = {};
  const yearResults = [];
  const playerBuilds = [];

  for (let year = 1; year <= GAME_YEARS; year++) {
    // Retirement happens at the start of the build phase, before new
    // builds (section 16.11). Each retirement frees a grid slot for
    // that year's builds; the slot cap is enforced by the reducer,
    // not here.
    const { portfolio: afterRetirement, removed: retiredThisYear } =
      removePortfolioInstances(portfolio, retirePlan[year] ?? []);
    const newlyBuilt = buildPlan[year] ?? [];
    portfolio = [...afterRetirement, ...newlyBuilt];

    const rolledOutputs = rollVariableOutputs(
      portfolio,
      plantsById,
      archetypeRng
    );

    const drawCount = DISASTER_DRAWS_PER_YEAR[year];
    let weightByCardId = null;
    let tierByCardId = null;
    if (year > 1 && remainingDeck.length >= 4) {
      tierByCardId = assignRiskTiers(remainingDeck, archetypeRng);
      const tierWeights = solveTierWeights({
        poolSize: remainingDeck.length,
        draws: drawCount
      });
      weightByCardId = tierWeightsToCardWeights({
        pool: remainingDeck,
        tierByCardId,
        tierWeights
      });
    }
    const { drawn, remaining } = drawDisasters(
      remainingDeck,
      drawCount,
      archetypeRng,
      weightByCardId
    );
    remainingDeck = remaining;

    const subsidyReformed = drawn.some(
      (d) => d.effect.kind === EFFECT_KIND.SUBSIDY_REMOVE
    );
    if (subsidyReformed) subsidyActive = false;
    subsidyByYear[year] = subsidyActive;

    const resolvedDrawn = drawn.map((d) =>
      resolvePersistentDisasterTarget({
        disaster: d,
        portfolio,
        plantsById,
        rng: archetypeRng
      })
    );
    const newPersistentDisasters = resolvedDrawn.filter(
      (d) =>
        d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER &&
        d.effect.persistent &&
        d.persistentTargetFamily
    );
    persistentDisasters = [...persistentDisasters, ...newPersistentDisasters];

    const r = resolveAdvancedYear({
      year,
      portfolio: [...portfolio],
      newlyBuiltThisYear: newlyBuilt,
      priorPlayerBuilds: [...playerBuilds],
      subsidyActive,
      disasters: [
        ...resolvedDrawn,
        ...persistentDisasters.filter(
          (d) => !newPersistentDisasters.some((fresh) => fresh.id === d.id)
        )
      ],
      rolledOutputs,
      plantsById,
      rng: archetypeRng,
      retiredThisYear,
      carriedCharge,
      carriedChargeUnits,
      ...(declineSchedule ? { declineSchedule } : {})
    });
    carriedCharge = r.carriedChargeForNextYear;
    carriedChargeUnits = r.carriedChargeUnitsForNextYear;
    playerBuilds.push(...newlyBuilt);
    yearResults.push({ ...r, subsidyReformed, tierByCardId });
  }

  const totalCapital = yearResults.reduce((s, y) => s + y.capital, 0);
  const totalResourceDevelopmentPremium = yearResults.reduce(
    (s, y) => s + (y.resourceDevelopmentPremium ?? 0),
    0
  );
  const totalOpex = yearResults.reduce((s, y) => s + y.opex, 0);
  const totalEnergyShortfall = yearResults.reduce(
    (s, y) => s + y.energyShortfall,
    0
  );
  const totalEnergyShortfallCost = yearResults.reduce(
    (s, y) => s + y.energyShortfallCost,
    0
  );
  const totalCapacityShortfall = yearResults.reduce(
    (s, y) => s + y.capacityShortfall,
    0
  );
  const totalCapacityShortfallCost = yearResults.reduce(
    (s, y) => s + y.capacityShortfallCost,
    0
  );
  const totalEmissions = yearResults.reduce((s, y) => s + y.emissions, 0);
  const totalDecommissionCost = yearResults.reduce(
    (s, y) => s + (y.decommissionCost ?? 0),
    0
  );

  // Escalating carbon price (section 16.10): each year's emissions
  // are weighted, then rounded to a whole unit so the integer-banded
  // Classic bracket table applies. Raw emissions are still reported
  // for display.
  const weightedEmissions = Math.round(
    yearResults.reduce(
      (s, y) =>
        s +
        (y.carbonEmissions ?? y.emissions) *
          (ADVANCED_CARBON_YEAR_WEIGHTS[y.year] ?? 1),
      0
    )
  );

  // Keep post-disaster generation diagnostics for result presentation
  // and analytics. Storage contributes no generation.
  const totalProducedOutput = yearResults.reduce(
    (s, y) => s + y.actualAnnualGeneration,
    0
  );
  const totalCleanEnergyOutput = yearResults.reduce(
    (s, y) => s + y.cleanEnergyOutput,
    0
  );
  const totalVariableCleanEnergyOutput = yearResults.reduce(
    (s, y) => s + y.variableCleanEnergyOutput,
    0
  );
  const totalFamilyOutput = {};
  for (const y of yearResults) {
    for (const [family, output] of Object.entries(y.familyOutput)) {
      totalFamilyOutput[family] = (totalFamilyOutput[family] ?? 0) + output;
    }
  }

  const bracket = lookupCarbonBracket(weightedEmissions, brackets);
  const carbonMultiplier = pickCarbonMultiplier(bracket, cop30);
  const carbonAmount = bracket.base * carbonMultiplier;

  const cleanEnergyAward = 0;
  const resilienceAward = 0;
  const affordabilityAward = 0;
  const totalAwards = 0;
  const energyDelivered = yearResults.reduce(
    (sum, year) =>
      sum + Math.max(0, year.annualEnergyDemand - year.energyShortfall),
    0
  );
  const costPerEnergyDelivered =
    energyDelivered > 0
      ? (totalCapital + totalResourceDevelopmentPremium + totalOpex) /
        energyDelivered
      : null;
  const cleanEnergyShare =
    totalProducedOutput > 0 ? totalCleanEnergyOutput / totalProducedOutput : 0;
  const variableCleanEnergyShare =
    totalProducedOutput > 0
      ? totalVariableCleanEnergyOutput / totalProducedOutput
      : 0;

  const totalCost =
    totalCapital +
    totalResourceDevelopmentPremium +
    totalOpex +
    totalDecommissionCost +
    totalEnergyShortfallCost +
    totalCapacityShortfallCost -
    carbonAmount -
    totalAwards;

  return {
    mode: GAME_MODE.ADVANCED_CAPACITY,
    gameVersion: ADVANCED_CAPACITY_GAME_VERSION,
    units: ADVANCED_UNITS,
    totalCost,
    totalCapital,
    totalResourceDevelopmentPremium,
    totalOpex,
    totalDecommissionCost,
    weightedEmissions,
    totalEnergyShortfall,
    totalEnergyShortfallCost,
    totalCapacityShortfall,
    totalCapacityShortfallCost,
    totalEmissions,
    bracket,
    cop30,
    carbonAmount,
    carbonMultiplier,
    totalProducedOutput,
    totalCleanEnergyOutput,
    totalVariableCleanEnergyOutput,
    totalFamilyOutput,
    cleanEnergyShare,
    variableCleanEnergyShare,
    cleanEnergyAward,
    cleanEnergyAwardTier: null,
    cleanEnergyAwardEligible: false,
    cleanEnergyAwardFamilies: 0,
    resilienceAward,
    resilienceAwardTier: null,
    resilienceFamilies: 0,
    resilienceLargestFamilyShare: 0,
    affordabilityAward,
    affordabilityAwardTier: null,
    affordabilityAwardEligible: false,
    energyDelivered,
    costPerEnergyDelivered,
    awardBreakdown: [],
    totalAwards,
    subsidyByYear,
    yearResults,
    finalPortfolio: portfolio,
    hadEnergyShortfall: totalEnergyShortfall > 0,
    hadCapacityShortfall: totalCapacityShortfall > 0
  };
}
